import { TaskPriority } from '@business/constants/task';
import { R } from '@core/utils/r';
import { SortByOptions, SortByDirections, CompactChecklistResponse, ChecklistResponse } from '@business/entities/checklist';
import { LinkableClassName, LinkableModuleType, TaskLinkableResponse } from '@business/entities/task';
import { toMoment } from '@core/utils/time';

export function getPriorityColor(priority: TaskPriority, input = false) {
  switch (priority) {
    case TaskPriority.urgent:
      return 'rgb(245, 0, 0)';
    case TaskPriority.high:
      return 'rgb(255, 204, 0)';
    case TaskPriority.normal:
      return 'rgb(111, 221, 255)';
    case TaskPriority.low:
      return 'rgb(216, 216, 216)';
    case TaskPriority.none:
      return input ? '' : '#00000025';
  }
}

export function getPriorityTitle(priority: TaskPriority) {
  switch (priority) {
    case TaskPriority.urgent:
      return 'Urgent';
    case TaskPriority.high:
      return 'High';
    case TaskPriority.normal:
      return 'Normal';
    case TaskPriority.low:
      return 'Low';
    case TaskPriority.none:
      return 'Clear';
  }
}

export function allPriorities(withClear = false) {
  if (withClear) {
    return [TaskPriority.urgent, TaskPriority.high, TaskPriority.normal, TaskPriority.low, TaskPriority.none];
  }

  return [TaskPriority.urgent, TaskPriority.high, TaskPriority.normal, TaskPriority.low];
}

export function getAllPriorityItems(withClear = true) {
  return R.map(allPriorities(withClear), p => {
    return { label: getPriorityTitle(p), value: p };
  });
}

export function getSortByOptionTitle(options: SortByOptions) {
  switch (options) {
    case SortByOptions.dueDate:
      return 'Due Date';
    case SortByOptions.title:
      return 'Title';
    case SortByOptions.dateAdded:
      return 'Date Added';
  }
}

export function getSortByDirectionTitle(options: SortByDirections) {
  switch (options) {
    case SortByDirections.asc:
      return 'ASC';
    case SortByDirections.desc:
      return 'DESC';
  }
}

export function allSortByOptions() {
  return [SortByOptions.dueDate, SortByOptions.title, SortByOptions.dateAdded];
}

export function allSortByDirections() {
  return [SortByDirections.asc, SortByDirections.desc];
}

export function getAllSortByItems() {
  return R.map(allSortByOptions(), p => {
    return { label: getSortByOptionTitle(p), value: p };
  });
}

export function getAllSortByDirectionItems() {
  return R.map(allSortByDirections(), p => {
    return { label: getSortByDirectionTitle(p), value: p };
  });
}

export function getFilteredChecklists(
  items: (ChecklistResponse | CompactChecklistResponse)[],
  options: {
    isCompleted?: boolean;
  },
) {
  const { isCompleted } = options;

  if (isCompleted) {
    return getFilteredCompletedChecklists(items);
  }
  const filtered = R.pipe(
    items,
    R.ops.filter(c => isCompleted === c.isCompleted),
    R.ops.filter(c => c.hasAttempted || c.status === 'needAction' || c.status === 'overDue'),
  );
  return filtered;
}
export function getFilteredCompletedChecklists(items: (ChecklistResponse | CompactChecklistResponse)[]) {
  const filtered = R.pipe(
    items,
    R.ops.filter(c => c.isCompleted),
  );
  return filtered;
}

export function getLinkableTypeFromClass(item: TaskLinkableResponse): LinkableModuleType {
  if (R.includes(item.type, LinkableClassName.DEAL)) {
    return LinkableModuleType.DEALS;
  } else if (R.includes(item.type, LinkableClassName.TASK)) {
    return LinkableModuleType.TASKS;
  } else if (R.includes(item.type, LinkableClassName.ORGANIZATION_PROJECT)) {
    return LinkableModuleType.PROJECTS;
  } else if (R.includes(item.type, LinkableClassName.SUPPORT_TICKET)) {
    return LinkableModuleType.SUPPORT_TICKETS;
  } else if (R.includes(item.type, LinkableClassName.PROPOSAL)) {
    return LinkableModuleType.PROPOSALS;
  } else if (R.includes(item.type, LinkableClassName.PROJECT_STEPS)) {
    return LinkableModuleType.PROJECT_STEPS;
  } else if (R.includes(item.type, LinkableClassName.SUBSCRIPTION_BOARD_ITEM)) {
    return LinkableModuleType.SUBSCRIPTION_BOARD_ITEMS;
  }

  return LinkableModuleType.TASKS;
}
// Task-specific time utilities
export function hasSpecificTime(timestamp?: number): boolean {
  if (!timestamp) return false;
  const moment = toMoment(timestamp);
  const endOfDay = moment.clone().endOf('day');
  return Math.abs(moment.unix() - endOfDay.unix()) > 60; // More than 1 minute difference from end of day
}

export function formatTaskDueDate(timestamp?: number, showTime: boolean = true): string {
  if (!timestamp) return '';
  const moment = toMoment(timestamp);
  const hasTime = hasSpecificTime(timestamp);

  if (hasTime && showTime) {
    return moment.format('M/D h:mma');
  } else {
    return moment.format('M/D');
  }
}

export function isTaskOverdue(dueDate?: number): boolean {
  if (!dueDate) return false;
  const now = toMoment().unix();
  return dueDate < now;
}

export function hasOverdueSubtasks(task: any): boolean {
  if (!task.children || task.children.length === 0) return false;
  return task.children.some((subtask: any) => !subtask.isCompleted && isTaskOverdue(subtask.dueDate));
}

export function isParentTaskOverdue(task: any): boolean {
  // Task is overdue if:
  // 1. The task itself is overdue, OR
  // 2. It has overdue subtasks
  const taskIsOverdue = !task.isCompleted && isTaskOverdue(task.dueDate);
  const hasOverdueChildren = hasOverdueSubtasks(task);
  return taskIsOverdue || hasOverdueChildren;
}

export function getOverdueTasksCount(tasks: any[]): number {
  return tasks.filter(task => isParentTaskOverdue(task)).length;
}

export function minutesToShortFormat(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
}

export function secondsToShortFormat(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return minutesToShortFormat(minutes);
}
